<template>
  <v-dialog
    :value="show"
    @input="$emit('update:show', $event)"
    persistent
    max-width="500"
    scrollable
  >
    <v-card rounded="lg">
      <!-- Header -->
      <v-card-title class="modal-header pa-0">
        <div class="d-flex align-center justify-space-between px-5 py-4 w-full">
          <div class="d-flex align-center">
            <v-avatar color="red lighten-1" size="38" class="mr-3">
              <v-icon dark small>mdi-minus-circle</v-icon>
            </v-avatar>
            <div>
              <h3 class="text-subtitle-1 font-weight-bold">
                {{ isEditMode ? "Edit Deduction" : "Add Custom Deduction" }}
              </h3>
              <span class="caption grey--text text--darken-1">
                {{
                  isEditMode
                    ? "Modify deduction details"
                    : "Add a new expense or fee"
                }}
              </span>
            </div>
          </div>
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>
      </v-card-title>

      <!-- Form Body -->
      <v-card-text class="pa-5">
        <v-form ref="form" v-model="isFormValid">
          <!-- Name/Description -->
          <v-text-field
            v-model="form.name"
            label="Name / Description"
            placeholder="e.g., Delivery Fee, Commission, etc."
            prepend-inner-icon="mdi-text"
            outlined
            dense
            :rules="[rules.required]"
            class="mb-1"
          ></v-text-field>

          <!-- Category -->
          <v-select
            v-model="form.category"
            label="Category"
            :items="categories"
            prepend-inner-icon="mdi-tag"
            outlined
            dense
            class="mb-1"
          ></v-select>

          <!-- Amount Type Toggle -->
          <div class="mb-4">
            <div class="caption grey--text text--darken-1 mb-2">
              Amount Type
            </div>
            <v-btn-toggle
              v-model="form.type"
              mandatory
              borderless
              class="amount-type-toggle"
            >
              <v-btn
                value="fixed"
                class="text-none px-5"
                :color="form.type === 'fixed' ? 'primary lighten-5' : ''"
                :class="form.type === 'fixed' ? 'primary--text' : ''"
                small
              >
                <v-icon left x-small>mdi-currency-php</v-icon>
                Fixed Amount
              </v-btn>
              <v-btn
                value="percentage"
                class="text-none px-5"
                :color="form.type === 'percentage' ? 'primary lighten-5' : ''"
                :class="form.type === 'percentage' ? 'primary--text' : ''"
                small
              >
                <v-icon left x-small>mdi-percent</v-icon>
                Percentage
              </v-btn>
            </v-btn-toggle>
          </div>

          <!-- Value Input -->
          <v-text-field
            v-model.number="form.value"
            :label="form.type === 'percentage' ? 'Percentage Value' : 'Amount'"
            type="number"
            min="0"
            step="0.01"
            :prefix="form.type === 'fixed' ? '₱' : ''"
            :suffix="form.type === 'percentage' ? '%' : ''"
            prepend-inner-icon="mdi-calculator"
            outlined
            dense
            :rules="[rules.required, rules.positive]"
            class="mb-1"
          ></v-text-field>

          <!-- Computed Amount Display (for percentage type) -->
          <v-expand-transition>
            <v-card
              v-if="form.type === 'percentage'"
              flat
              class="computed-amount-card pa-3 mb-4"
              rounded="lg"
            >
              <div class="d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                  <v-icon small color="grey darken-1" class="mr-2"
                    >mdi-calculator-variant</v-icon
                  >
                  <span class="body-2 grey--text text--darken-1"
                    >Computed Amount</span
                  >
                </div>
                <span class="body-1 font-weight-bold primary--text">
                  {{ formatCurrency(computedAmount) }}
                </span>
              </div>
              <div class="caption grey--text mt-1">
                {{ form.value || 0 }}% of {{ formatCurrency(totalQuotePrice) }}
              </div>
            </v-card>
          </v-expand-transition>

          <!-- Deduction Mode Toggle -->
          <div class="mb-4">
            <div class="caption grey--text text--darken-1 mb-2">
              Deduction Mode
            </div>
            <v-btn-toggle
              v-model="form.deduction_mode"
              mandatory
              borderless
              class="deduction-mode-toggle"
            >
              <v-btn
                value="auto_deduct"
                class="text-none px-5"
                :color="
                  form.deduction_mode === 'auto_deduct' ? 'blue lighten-5' : ''
                "
                :class="
                  form.deduction_mode === 'auto_deduct' ? 'blue--text' : ''
                "
                small
              >
                <v-icon left x-small>mdi-cash-check</v-icon>
                Auto-Deduct
              </v-btn>
              <v-btn
                value="capital_expenditure"
                class="text-none px-5"
                :color="
                  form.deduction_mode === 'capital_expenditure'
                    ? 'orange lighten-5'
                    : ''
                "
                :class="
                  form.deduction_mode === 'capital_expenditure'
                    ? 'orange--text text--darken-2'
                    : ''
                "
                small
              >
                <v-icon left x-small>mdi-cash-minus</v-icon>
                Capital Expenditure
              </v-btn>
            </v-btn-toggle>
            <div class="caption grey--text mt-2">
              {{
                form.deduction_mode === "capital_expenditure"
                  ? "Paid upfront — added to Capital Expenditure total"
                  : "Deducted from cheque upon payment receipt"
              }}
            </div>
          </div>

          <!-- Notes -->
          <v-textarea
            v-model="form.notes"
            label="Notes (optional)"
            placeholder="Additional remarks or details..."
            prepend-inner-icon="mdi-note-text"
            outlined
            dense
            rows="2"
            auto-grow
          ></v-textarea>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Actions -->
      <v-card-actions class="px-5 py-4">
        <v-spacer></v-spacer>
        <v-btn text class="text-none mr-2" @click="close"> Cancel </v-btn>
        <v-btn
          color="primary"
          depressed
          class="text-none px-5"
          :disabled="!isFormValid"
          @click="save"
        >
          <v-icon left small>mdi-check</v-icon>
          {{ isEditMode ? "Update Deduction" : "Add Deduction" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "AddDeductionModal",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    editDeduction: {
      type: Object,
      default: null,
    },
    totalQuotePrice: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isFormValid: false,
      form: {
        id: null,
        name: "",
        type: "fixed",
        value: null,
        category: "other",
        deduction_mode: "auto_deduct",
        notes: "",
      },
      categories: [
        { text: "Logistics", value: "logistics" },
        { text: "Commission", value: "commission" },
        { text: "Tax / Government", value: "tax" },
        { text: "Service Fee", value: "service" },
        { text: "Discount", value: "discount" },
        { text: "Other", value: "other" },
      ],
      rules: {
        required: (v) =>
          (v !== null && v !== "" && v !== undefined) ||
          "This field is required",
        positive: (v) => v >= 0 || "Value must be 0 or greater",
      },
    };
  },
  computed: {
    isEditMode() {
      return !!this.editDeduction;
    },
    computedAmount() {
      if (this.form.type === "percentage") {
        return (
          (this.totalQuotePrice * (parseFloat(this.form.value) || 0)) / 100
        );
      }
      return parseFloat(this.form.value) || 0;
    },
  },
  watch: {
    show(val) {
      if (val) {
        if (this.editDeduction) {
          // Populate form with existing deduction data
          this.form = {
            id: this.editDeduction.id,
            name: this.editDeduction.name || "",
            type: this.editDeduction.type || "fixed",
            value: this.editDeduction.value || 0,
            category: this.editDeduction.category || "other",
            deduction_mode: this.editDeduction.deduction_mode || "auto_deduct",
            notes: this.editDeduction.notes || "",
          };
        } else {
          // Reset form for new deduction
          this.resetForm();
        }
      }
    },
  },
  methods: {
    resetForm() {
      this.form = {
        id: null,
        name: "",
        type: "fixed",
        value: null,
        category: "other",
        deduction_mode: "auto_deduct",
        notes: "",
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    close() {
      this.$emit("update:show", false);
      this.$emit("close");
    },
    save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      const deduction = {
        id: this.form.id || this.generateId(),
        name: this.form.name,
        type: this.form.type,
        value: parseFloat(this.form.value) || 0,
        computed_amount: this.computedAmount,
        category: this.form.category,
        deduction_mode: this.form.deduction_mode,
        notes: this.form.notes,
      };

      this.$emit("save", deduction);
      this.close();
    },
    generateId() {
      // Generate a simple unique ID
      return (
        "ded_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9)
      );
    },
    formatCurrency(val) {
      const num = parseFloat(val) || 0;
      return (
        "₱ " +
        num.toLocaleString("en-PH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-header {
  background: #fafbfc;
}

.w-full {
  width: 100%;
}

.amount-type-toggle {
  border-radius: 8px !important;
  overflow: hidden;
  border: 1px solid #e8eaed;

  .v-btn {
    border-radius: 8px !important;
    letter-spacing: 0;
  }
}

.computed-amount-card {
  background: #f5f5f5;
  border: 1px solid #e8eaed;
}

.deduction-mode-toggle {
  border-radius: 8px !important;
  overflow: hidden;
  border: 1px solid #e8eaed;

  .v-btn {
    border-radius: 8px !important;
    letter-spacing: 0;
  }
}
</style>
